import React, { useState, useEffect } from 'react';
import {
  Table, Button, Space, Modal, Form, Input, Select, InputNumber,
  Switch, message, Popconfirm, Tag, Tree
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, FolderOutlined,
  FileOutlined, ApiOutlined
} from '@ant-design/icons';
import api from '../../utils/api';

const { Option } = Select;

const Menus = () => {
  const [loading, setLoading] = useState(false);
  const [menus, setMenus] = useState([]);
  const [menuTree, setMenuTree] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingMenu, setEditingMenu] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchMenus();
  }, []);

  const fetchMenus = async () => {
    setLoading(true);
    try {
      const [treeRes, listRes] = await Promise.all([
        api.get('/admin/menus/tree'),
        api.get('/admin/menus')
      ]);
      
      setMenuTree(treeRes.data);
      setMenus(listRes.data);
    } catch (error) {
      message.error('获取菜单列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = (parentMenu = null) => {
    setEditingMenu(null);
    form.resetFields();
    if (parentMenu) {
      form.setFieldsValue({ parent_id: parentMenu.id });
    }
    setModalVisible(true);
  };

  const handleEdit = (menu) => {
    setEditingMenu(menu);
    form.setFieldsValue(menu);
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/admin/menus/${id}`);
      message.success('删除成功');
      fetchMenus();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingMenu) {
        await api.put(`/admin/menus/${editingMenu.id}`, values);
        message.success('更新成功');
      } else {
        await api.post('/admin/menus', values);
        message.success('创建成功');
      }
      
      setModalVisible(false);
      fetchMenus();
    } catch (error) {
      message.error('操作失败');
    }
  };

  const getMenuTypeIcon = (type) => {
    switch (type) {
      case 1:
        return <FolderOutlined style={{ color: '#1890ff' }} />;
      case 2:
        return <FileOutlined style={{ color: '#52c41a' }} />;
      case 3:
        return <ApiOutlined style={{ color: '#faad14' }} />;
      default:
        return null;
    }
  };

  const getMenuTypeTag = (type) => {
    const types = {
      1: { text: '目录', color: 'blue' },
      2: { text: '菜单', color: 'green' },
      3: { text: '按钮', color: 'orange' }
    };
    const typeInfo = types[type] || { text: '未知', color: 'default' };
    return <Tag color={typeInfo.color}>{typeInfo.text}</Tag>;
  };

  const columns = [
    {
      title: '菜单名称',
      dataIndex: 'menu_name',
      key: 'menu_name',
      width: 200,
      render: (text, record) => (
        <Space>
          {getMenuTypeIcon(record.menu_type)}
          {text}
        </Space>
      )
    },
    {
      title: '菜单编码',
      dataIndex: 'menu_code',
      key: 'menu_code',
      width: 150
    },
    {
      title: '类型',
      dataIndex: 'menu_type',
      key: 'menu_type',
      width: 80,
      render: (type) => getMenuTypeTag(type)
    },
    {
      title: '图标',
      dataIndex: 'icon',
      key: 'icon',
      width: 100
    },
    {
      title: '路由地址',
      dataIndex: 'route_path',
      key: 'route_path',
      width: 180
    },
    {
      title: '权限标识',
      dataIndex: 'permission',
      key: 'permission',
      width: 180,
      render: (text) => text ? <Tag color="purple">{text}</Tag> : '-'
    },
    {
      title: '排序',
      dataIndex: 'sort_order',
      key: 'sort_order',
      width: 80,
      align: 'center'
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      align: 'center',
      render: (status) => (
        <Tag color={status === 1 ? 'success' : 'error'}>
          {status === 1 ? '启用' : '禁用'}
        </Tag>
      )
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          {record.menu_type !== 3 && (
            <Button
              type="link"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => handleAdd(record)}
            >
              添加
            </Button>
          )}
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个菜单吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  // 转换树形数据为表格数据
  const convertTreeToTableData = (tree, level = 0) => {
    let result = [];
    tree.forEach(node => {
      result.push({ ...node, level });
      if (node.children && node.children.length > 0) {
        result = result.concat(convertTreeToTableData(node.children, level + 1));
      }
    });
    return result;
  };

  const tableData = convertTreeToTableData(menuTree);

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => handleAdd()}
        >
          新增菜单
        </Button>
      </div>

      <Table
        loading={loading}
        columns={columns}
        dataSource={tableData}
        rowKey="id"
        pagination={false}
        scroll={{ x: 1400 }}
        rowClassName={(record) => `menu-level-${record.level}`}
      />

      <Modal
        title={editingMenu ? '编辑菜单' : '新增菜单'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={700}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            parent_id: 0,
            menu_type: 2,
            sort_order: 0,
            is_visible: 1,
            is_cache: 0,
            status: 1
          }}
        >
          <Form.Item
            label="上级菜单"
            name="parent_id"
          >
            <Select
              placeholder="选择上级菜单（不选则为顶级菜单）"
              allowClear
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              <Option value={0}>顶级菜单</Option>
              {menus
                .filter(m => m.menu_type !== 3)
                .map(menu => (
                  <Option key={menu.id} value={menu.id}>
                    {menu.menu_name}
                  </Option>
                ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="菜单类型"
            name="menu_type"
            rules={[{ required: true, message: '请选择菜单类型' }]}
          >
            <Select>
              <Option value={1}>目录</Option>
              <Option value={2}>菜单</Option>
              <Option value={3}>按钮</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="菜单名称"
            name="menu_name"
            rules={[{ required: true, message: '请输入菜单名称' }]}
          >
            <Input placeholder="请输入菜单名称" />
          </Form.Item>

          <Form.Item
            label="菜单编码"
            name="menu_code"
            rules={[{ required: true, message: '请输入菜单编码' }]}
          >
            <Input placeholder="请输入菜单编码（唯一标识）" />
          </Form.Item>

          <Form.Item
            label="菜单图标"
            name="icon"
          >
            <Input placeholder="请输入图标名称（如：UserOutlined）" />
          </Form.Item>

          <Form.Item
            label="路由地址"
            name="route_path"
          >
            <Input placeholder="请输入路由地址" />
          </Form.Item>

          <Form.Item
            label="组件路径"
            name="component_path"
          >
            <Input placeholder="请输入组件路径" />
          </Form.Item>

          <Form.Item
            label="权限标识"
            name="permission"
          >
            <Input placeholder="请输入权限标识（如：user:list:view）" />
          </Form.Item>

          <Form.Item
            label="排序号"
            name="sort_order"
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            label="是否可见"
            name="is_visible"
            valuePropName="checked"
            getValueFromEvent={(checked) => checked ? 1 : 0}
            getValueProps={(value) => ({ checked: value === 1 })}
          >
            <Switch checkedChildren="显示" unCheckedChildren="隐藏" />
          </Form.Item>

          <Form.Item
            label="是否缓存"
            name="is_cache"
            valuePropName="checked"
            getValueFromEvent={(checked) => checked ? 1 : 0}
            getValueProps={(value) => ({ checked: value === 1 })}
          >
            <Switch checkedChildren="缓存" unCheckedChildren="不缓存" />
          </Form.Item>

          <Form.Item
            label="状态"
            name="status"
            valuePropName="checked"
            getValueFromEvent={(checked) => checked ? 1 : 0}
            getValueProps={(value) => ({ checked: value === 1 })}
          >
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>

          <Form.Item
            label="备注"
            name="remark"
          >
            <Input.TextArea rows={3} placeholder="请输入备注" />
          </Form.Item>
        </Form>
      </Modal>

      <style jsx>{`
        .menu-level-0 {
          font-weight: bold;
        }
        .menu-level-1 {
          padding-left: 20px;
        }
        .menu-level-2 {
          padding-left: 40px;
        }
      `}</style>
    </div>
  );
};

export default Menus;
